import { useQuery } from '@apollo/client';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRICING_TABLE_BY_ID } from '@gql/pricingTable';
import {
    ArrowBack as ArrowBackIcon,
    ContentCopy as CopyIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItem,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PricingTable } from '../../../types/pricingTable';

interface GetPricingTableByIdData {
    getPricingTableById: PricingTable;
}

const PricingTableDetailModule: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { showSnackbar } = useSnackbar();

    const { data, loading, error } = useQuery<GetPricingTableByIdData>(GET_PRICING_TABLE_BY_ID, {
        variables: { _id: id },
        skip: !id,
        errorPolicy: 'all',
    });

    const handleCopyId = async (id: string) => {
        try {
            await navigator.clipboard.writeText(id);
            // You might want to show a toast notification here
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const handleCopyTableName = async (tableName: string) => {
        try {
            await navigator.clipboard.writeText(tableName);
            // You might want to show a toast notification here
        } catch (err) {
            console.error('Failed to copy table name:', err);
        }
    };


    const handleCopyStripeId = async (stripeId: string) => {
        try {
            await navigator.clipboard.writeText(stripeId);
            showSnackbar('Stripe ID copied to clipboard', 'success');
        } catch (err) {
            console.error('Failed to copy Stripe ID:', err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Error loading pricing table: {error.message}
                </Alert>
            </Box>
        );
    }

    if (!data?.getPricingTableById) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Pricing table not found
                </Alert>
            </Box>
        );
    }

    const pricingTable = data.getPricingTableById;

    return (
        <Box p={3}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate('/pricing-tables')}>
                    <ArrowBackIcon />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h4" component="h1">
                        {pricingTable.tableName}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Pricing table details and configuration
                    </Typography>
                </Box>
                {/* <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/pricing-tables/edit/${pricingTable._id}`)}
                >
                    Edit Table
                </Button> */}
            </Box>

            {/* Basic Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Table Name
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {pricingTable.tableName}
                                </Typography>
                                <Tooltip title="Copy Table Name">
                                    <IconButton size="small" onClick={() => handleCopyTableName(pricingTable.tableName)}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Table ID
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        padding: '4px 8px',
                                        borderRadius: 1,
                                    }}
                                >
                                    {pricingTable._id}
                                </Typography>
                                <Tooltip title="Copy ID">
                                    <IconButton size="small" onClick={() => handleCopyId(pricingTable._id)}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Description
                            </Typography>
                            <Typography variant="body1">
                                {pricingTable.tableDescription || 'No description provided'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Plans Overview */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Plans Overview ({pricingTable.plans.length} plans)
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {pricingTable.plans.map((pricingPlan, index) => {
                        const plan = pricingPlan.plan;
                        return (
                            <Grid item xs={12} md={6} lg={4} key={plan._id}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <Typography variant="h6" component="h3">
                                                {plan.name}
                                            </Typography>
                                            <Stack direction="row" spacing={1}>
                                                {plan.highlight && (
                                                    <Chip label="Highlight" color="secondary" size="small" />
                                                )}

                                                {plan.contact && (
                                                    <Chip label="Contact" size="small" />
                                                )}
                                                <Chip label={plan.type} size="small" variant="outlined" />
                                            </Stack>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {plan.description}
                                        </Typography>
                                        <List>
                                            {plan.prices.map(price => (
                                                <ListItem key={price._id} sx={{
                                                    px: 0,
                                                }}>
                                                    {(price.unitAmount / 100).toLocaleString('fr-FR', {
                                                        style: 'currency',
                                                        currency: price.currency,
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}{price.recurring ? ' / ' + price.recurring : ''}
                                                    <Box display="inline-flex" alignItems="center" ml={1}>
                                                        <Chip
                                                            label={
                                                                <>
                                                                    {price.stripeId}
                                                                    <Tooltip title="Copy Stripe ID">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleCopyStripeId(price.stripeId)}
                                                                            sx={{ ml: 0.5 }}
                                                                        >
                                                                            <CopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            }
                                                            size="small"
                                                            onClick={() => handleCopyStripeId(price.stripeId)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    backgroundColor: 'action.hover'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {pricingTable.plans.length === 0 && (
                    <Alert severity="info">
                        No plans configured for this pricing table.
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default PricingTableDetailModule;
